import axios from 'axios';

// Re-use the same backend URL resolution as the consumer API client, but
// keep a SEPARATE axios instance + a SEPARATE localStorage token so the two
// apps (consumer & business) can coexist in the same browser tab without
// stomping on each other.
const PRODUCTION_API_FALLBACK = 'https://business.becaim.app';

// See api.js's resolveBackendUrl for the full rationale — kept in sync here.
// An explicit absolute REACT_APP_BACKEND_URL baked in at build time always
// wins (native included, so `*:local-debug` builds work); only a missing/
// relative env value falls back to production.
function resolveBackendUrl() {
  const fromEnv = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (fromEnv && /^https?:\/\//i.test(fromEnv)) return fromEnv.replace(/\/+$/, '');
  return PRODUCTION_API_FALLBACK;
}

export const BACKEND_URL = resolveBackendUrl();
export const BUSINESS_API = `${BACKEND_URL}/api/business`;

export const BIZ_TOKEN_KEY = 'becaim_biz_token';
export const BIZ_USER_KEY = 'becaim_biz_user';
export const BIZ_TENANT_KEY = 'becaim_biz_tenant';

export const bizApi = axios.create({ baseURL: BACKEND_URL + '/api' });

bizApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(BIZ_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Tell the backend which locale to generate AI content in (missions,
  // coach replies, team-learning insights, forecasts). Fallback to `en`
  // if the setting hasn't been persisted yet.
  try {
    const lang = (localStorage.getItem('becaim_biz_lang') || 'en').split('-')[0];
    if (lang) config.headers['X-User-Lang'] = lang;
  } catch (_) { /* ignore */ }
  return config;
});

/** FastAPI 422 bodies carry `detail` as an ARRAY OF OBJECTS. Every call site
 *  toasts/renders `data.detail`, and rendering an object throws React error
 *  #31 (blank page). Normalise it to a readable string once, here. */
/** Localize a backend error message for the active language.
 *  Backend `detail` strings stay English on the wire (they are matched by
 *  exact text, or by pattern when the message embeds a value); the locale
 *  bundle's `api_errors` section carries the translation. Unknown messages
 *  pass through unchanged, so nothing is ever hidden. */
export function localizeApiMessage(msg) {
  if (typeof msg !== 'string' || !msg) return msg;
  try {
    // eslint-disable-next-line global-require
    const i18n = require('@/i18n').default;
    if ((i18n.language || 'en').split('-')[0] === 'en') return msg;
    const exact = i18n.t(`api_errors.${msg}`, { defaultValue: '' });
    if (exact) return exact;
    const table = i18n.getResourceBundle(i18n.language.split('-')[0], 'common')?.api_errors;
    if (table) {
      for (const [en, pt] of Object.entries(table)) {
        if (!en.includes('{{x}}')) continue;
        const parts = en.split('{{x}}').map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        const m = msg.match(new RegExp('^' + parts.join('(.+?)') + '$'));
        if (m) { let i = 0; return pt.replace(/\{\{x\}\}/g, () => m[++i] ?? ''); }
      }
    }
  } catch (_) { /* fall through */ }
  return msg;
}

function normaliseDetail(data) {
  const d = data?.detail;
  if (d == null) return;
  if (typeof d === 'string') { data.detail = localizeApiMessage(d); return; }
  const one = (item) => {
    if (typeof item === 'string') return item;
    const field = Array.isArray(item?.loc)
      ? item.loc.filter((p) => p !== 'body').join('.') : '';
    const msg = item?.msg || item?.message || JSON.stringify(item);
    return field ? `${field}: ${msg}` : msg;
  };
  data.detail = localizeApiMessage(Array.isArray(d)
    ? d.map(one).join(' · ')
    : one(d));
}

bizApi.interceptors.response.use(
  (r) => r,
  (err) => {
    try { normaliseDetail(err?.response?.data); } catch (_) { /* ignore */ }
    if (err?.response?.status === 401) {
      const path = window.location.pathname || '';
      // Only auto-redirect when inside the /business app — otherwise the
      // consumer flow handles its own 401s.
      if (path.startsWith('/business') && !path.startsWith('/business/login') && !path.startsWith('/business/signup') && !path.startsWith('/business/accept-invite')) {
        localStorage.removeItem(BIZ_TOKEN_KEY);
        localStorage.removeItem(BIZ_USER_KEY);
        localStorage.removeItem(BIZ_TENANT_KEY);
        window.location.href = '/business/login';
      }
    }
    return Promise.reject(err);
  }
);
